import { useEffect, useRef } from "react";
import { useFormikContext } from "formik";
import isEqual from "lodash/isEqual";

import { fetchAssetValues } from "../utils/asset";

/**
 * Field-level merge of incoming server state into a live asset form.
 *
 * Replaces Formik's all-or-nothing `enableReinitialize`. When the asset object
 * changes (initial load, a save response, or a real-time `assetMutated`
 * subscription triggered by another client / the mobile app), only fields the
 * user has NOT edited locally adopt the new server value. Fields the user is
 * actively editing (dirty relative to the tracked baseline) are preserved, so a
 * remote update can never silently wipe unsaved work.
 *
 * The baseline is shared via `baselineRef` so the save path can build the sparse
 * patch / baseValues from the same per-field snapshot. The ref holds
 * `{ assetId, values }`; whenever the asset id changes the baseline resets,
 * which keeps switching between assets correct even without a remount.
 *
 * @param {Object} props
 * @param {Object} props.asset       - The current server asset object
 * @param {{ current: { assetId: string, values: Object } | null }} props.baselineRef
 */
export default function RemoteAssetSync({ asset, baselineRef }) {
  const { values, setValues } = useFormikContext();
  const valuesRef = useRef(values);
  valuesRef.current = values;

  useEffect(() => {
    if (!asset?.id) return;

    const incoming = fetchAssetValues(asset);
    const stored = baselineRef.current;
    const currentValues = valuesRef.current;

    // First load for this asset (or after switching assets): adopt everything.
    if (!stored || stored.assetId !== asset.id) {
      baselineRef.current = { assetId: asset.id, values: incoming };
      setValues(incoming);
      return;
    }

    const baseline = stored.values;
    const nextValues = { ...currentValues };
    const nextBaseline = { ...baseline };
    let changed = false;

    Object.keys(incoming).forEach(field => {
      const userTouched = !isEqual(currentValues[field], baseline[field]);
      if (userTouched) return;

      nextBaseline[field] = incoming[field];
      if (!isEqual(currentValues[field], incoming[field])) {
        nextValues[field] = incoming[field];
        changed = true;
      }
    });

    baselineRef.current = { assetId: asset.id, values: nextBaseline };
    if (changed) setValues(nextValues);
  }, [asset, baselineRef, setValues]);

  return null;
}
